import * as React from "react"
import {Dialog} from "@headlessui/react"

import {twMerge} from "libs/twmerge"
import {CloseIcon} from "../icons"
import {useWindowSize} from "hooks/useWindowSize"

/**
 * @typedef {object} ResponsiveModalProps
 * @property {boolean} open
 * @property {boolean} [closeWithbackdrop]
 * @property {() => void} [onClose]
 * @property {string} [panelClassName]
 * @property {React.HTMLAttributes<HTMLDivElement>["style"]} [panelStyle]
 * @property {React.ReactNode} children
 * @property {React.ReactNode} [leftAction]
 * @property {React.ReactNode} [rightAction]
 * @property {(event: React.BaseSyntheticEvent<object, any, any> | undefined) => void} [onSubmit]
 * @property {string} [closeButtonClassName]
 * @property {boolean} [withPadding]
 */

/**
 * @type {React.FC<ResponsiveModalProps> & {
 *  Title: React.FC<TitleProps>
 * }}
 */
const ResponsiveModal = ({
  panelClassName = "",
  panelStyle,
  children,
  open,
  closeWithbackdrop = false,
  leftAction,
  rightAction,
  onSubmit,
  onClose = () => {},
  closeButtonClassName = "",
  withPadding = true,
}) => {
  const {height} = useWindowSize()
  return (
    <Dialog
      open={open}
      onClose={closeWithbackdrop ? onClose : () => {}}
      className="relative z-40"
    >
      <Dialog.Backdrop
        className="fixed top-0 right-0 bottom-0 left-0 bg-dark bg-opacity-20 dark:bg-opacity-80 z-30"
        aria-hidden={true}
      />
      <Wrapper onSubmit={onSubmit}>
        <div
          className="flex h-full items-end lg:items-center justify-center lg:p-16"
          style={{height: height || "100vh"}}
        >
          <Dialog.Panel
            className={`
              flex flex-col justify-between bg-white w-screen overflow-y-auto lg:max-w-full h-full lg:rounded-3xl
              dark:bg-midnight
              ${panelClassName}
            `}
            style={panelStyle}
          >
            <div
              className={twMerge(
                `relative overflow-auto flex flex-col flex-grow`,
                withPadding ? `px-16 py-8` : undefined,
              )}
            >
              {/* Header */}
              <div className="absolute top-8 right-16 z-10">
                <button
                  type="button"
                  data-testid="modal-close-button"
                  className={twMerge(
                    "outline-none text-blue-night",
                    closeButtonClassName,
                  )}
                  onClick={onClose}
                >
                  <CloseIcon className="h-12 w-12 lg:w-6 lg:h-6 stroke-blue-night dark:stroke-white" />
                </button>
              </div>

              {children}
            </div>
            {leftAction || rightAction ? (
              <div className="border-t border-gray-light dark:border-dark px-14 py-4 flex flex-row items-center justify-between lg:rounded-b-3xl">
                {leftAction ?? <div />}
                {rightAction ?? <div />}
              </div>
            ) : null}
          </Dialog.Panel>
        </div>
      </Wrapper>
    </Dialog>
  )
}

/**
 *
 * @type {React.FC<{children: React.ReactNode, onSubmit?: React.FormEventHandler<HTMLFormElement> }>}
 */
const Wrapper = ({onSubmit, children}) => {
  const onSubmitRef = React.useRef(onSubmit)
  React.useEffect(() => {
    onSubmitRef.current = onSubmit
  })

  /**
   * @type {React.FormEventHandler<HTMLFormElement>}
   */
  const handleSubmit = React.useCallback(e => {
    e?.preventDefault()
    onSubmitRef.current?.(e)
  }, [])

  return onSubmit ? (
    <form
      onSubmit={handleSubmit}
      className="fixed top-0 right-0 bottom-0 left-0"
      noValidate
    >
      {children}
    </form>
  ) : (
    <div className="fixed top-0 right-0 bottom-0 left-0">{children}</div>
  )
}

/**
 * @typedef {React.PropsWithChildren<{
 *  className?: string
 * }>} TitleProps
 */

/** @type {React.FC<TitleProps>} */
const Title = ({className, children}) => {
  return (
    <h1 className={twMerge("typo-title-1 mr-12", className)}>{children}</h1>
  )
}

ResponsiveModal.Title = Title

export default ResponsiveModal
